import type { BankDayShift } from '@fd/finance-core';
import type { Account, Category, Person, Transaction } from '@fd/schemas';
import { AlertTriangle, CalendarClock, CloudUpload } from 'lucide-react';
import type { Synced } from '../app/data.ts';
import { useT } from '../app/i18n.tsx';
import { memberName } from '../app/member-name.ts';
import { settlementRoute } from '../app/settlement-route.ts';
import { MerchantIcon } from './BrandLogo.tsx';
import { CategoryIcon, Money } from './primitives.tsx';

export function categoryLabel(t: ReturnType<typeof useT>, category: Category | undefined): string {
  if (!category) return t.t('transactions.uncategorised');
  return category.name ?? (category.key ? t.category(category.key) : t.t('transactions.uncategorised'));
}

/** How a settlement payment looks: coins in the settlement colour (it has no category). */
export const SETTLEMENT_LOOK = { icon: 'hand-coins', color: '#0e7c66' } as const;

/** "Due 29 Aug: weekend and bank holiday · payment for August 2026" — why the bank took it on another day. */
export function shiftLabel(t: ReturnType<typeof useT>, shift: BankDayShift, bookingDate: string): string {
  const why = t.t(`transactions.shifted.${shift.reason}`, { date: t.date(shift.dueDate, 'dayMonth') });
  if (shift.dueDate.slice(0, 7) === bookingDate.slice(0, 7)) return why;
  return `${why} · ${t.t('transactions.shifted.forMonth', { month: t.date(`${shift.dueDate.slice(0, 7)}-01`, 'monthYear') })}`;
}

/** A recurring payment the bank moved off its due day (weekend, bank holiday); the reason on hover. */
export function ShiftBadge({ shift, bookingDate }: { shift: BankDayShift | null | undefined; bookingDate: string }) {
  const t = useT();
  if (!shift) return null;
  const label = shiftLabel(t, shift, bookingDate);
  return (
    <span className="badge badge--shifted" title={label} aria-label={label}>
      <CalendarClock aria-hidden="true" />
      {t.t('transactions.shifted.badge')}
    </span>
  );
}

export function TransactionRow({
  transaction,
  category,
  account,
  accounts = [],
  persons,
  shift,
  showDate = true,
  onOpen,
}: {
  transaction: Synced<Transaction>;
  category: Category | undefined;
  account: Account | undefined;
  /** All accounts, so a transfer between the household's people shows the person (member-name.ts). */
  accounts?: readonly Account[];
  persons: readonly Person[];
  shift?: BankDayShift | null;
  /** Off in a list grouped by day, where the day's own heading carries the date. */
  showDate?: boolean;
  onOpen?(): void;
}) {
  const t = useT();
  const payer = persons.find((p) => p.id === transaction.paidByPersonId);
  const isTransfer = transaction.kind === 'TRANSFER';
  const isSettlement = transaction.kind === 'SETTLEMENT_PAYMENT';
  const icon = isTransfer ? 'transfer' : isSettlement ? SETTLEMENT_LOOK.icon : (category?.icon ?? null);
  const color = isTransfer ? '#4a3aa7' : isSettlement ? SETTLEMENT_LOOK.color : (category?.color ?? null);
  const pending = transaction.syncState === 'pending' || transaction.syncState === 'syncing';
  const problem = transaction.syncState === 'failed' || transaction.syncState === 'conflict';
  return (
    <div
      className={`list-row${onOpen ? ' list-row--interactive' : ''}`}
      onClick={onOpen}
      role={onOpen ? 'button' : undefined}
      tabIndex={onOpen ? 0 : undefined}
      onKeyDown={(event) => {
        if (onOpen && (event.key === 'Enter' || event.key === ' ')) {
          event.preventDefault();
          onOpen();
        }
      }}
    >
      {isSettlement ? <CategoryIcon icon={icon} color={color} /> : <MerchantIcon texts={[transaction.merchantName, transaction.description]} icon={icon} color={color} />}
      <div className="stack grow" style={{ gap: 2, minWidth: 0 }}>
        <div className="row gap-2" style={{ minWidth: 0 }}>
          <span className="list-row__title truncate">{memberName(transaction, accounts, persons) ?? transaction.merchantName ?? transaction.description ?? '—'}</span>
        </div>
        {/* The badges open the second line, before the category: the name keeps the whole first line (the owner,
            02.10.2026: „oczekuje, wspólna ucinają nazwy”); the category text gives way instead. */}
        <div className="row gap-2 list-row__badges" style={{ minWidth: 0 }}>
          {transaction.status === 'PENDING' && <span className="badge badge--pending">{t.t('transactions.badge.pending')}</span>}
          {transaction.needsReview && <span className="badge badge--pending">{t.t('banks.review.badge')}</span>}
          {transaction.expenseType === 'SHARED' && !isTransfer && <span className="badge badge--shared">{t.t('transactions.badge.shared')}</span>}
          <ShiftBadge shift={shift} bookingDate={transaction.bookingDate} />
          {/* A transfer says so in the line below ("Own transfer · …"); no badge repeating it. */}
          {transaction.kind === 'REFUND' && <span className="badge badge--refund">{t.t('transactions.badge.refund')}</span>}
          {pending && (
            <span className="badge" title={t.t('sync.pending')}>
              <CloudUpload aria-hidden="true" />
              <span className="visually-hidden">{t.t('sync.pending')}</span>
            </span>
          )}
          {problem && (
            <span className="badge badge--error">
              <AlertTriangle aria-hidden="true" />
              {t.t(transaction.syncState === 'conflict' ? 'sync.conflict' : 'sync.failed')}
            </span>
          )}
          <span className="list-row__meta truncate">
            {[
              isTransfer || isSettlement ? t.dynamic(`transactions.kind.${transaction.kind}`) : categoryLabel(t, category),
              isSettlement ? settlementRoute(transaction, account, persons) : account?.name,
              payer ? `${t.t('transactions.paidBy')}: ${payer.displayName}` : null,
            ]
              .filter(Boolean)
              .join(' · ')}
          </span>
        </div>
      </div>
      <div className="stack" style={{ alignItems: 'flex-end', gap: 2 }}>
        <span className="list-row__amount">
          <Money minor={transaction.amountMinor} currency={transaction.currency} tone="signed" />
        </span>
        {showDate && <span className="list-row__meta">{t.date(transaction.bookingDate, 'dayMonth')}</span>}
      </div>
    </div>
  );
}
